import { useState, useEffect } from 'react';
import { ordersApi, varietiesApi, storeApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Phone, Plus, Trash2, Copy, CheckCircle2, Truck, Store } from 'lucide-react';

const EMPTY = { customer_name: '', phone: '', email: '', delivery_method: 'delivery', address: '', suburb: '', postcode: '', scheduled_date: '', notes: '', admin_notes: '', status: 'confirmed', fee_override: '' };
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

function OrderNumberReadout({ order, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(order.order_number); setCopied(true); toast.success('Order number copied'); };
  return (
    <div className="text-center space-y-4 py-4" data-testid="manual-order-success">
      <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
      <div>
        <p className="text-sm text-muted-foreground mb-1">Read this order number to the customer</p>
        <p className="font-heading text-4xl font-bold tracking-wide text-primary" data-testid="manual-order-number">{order.order_number}</p>
      </div>
      <p className="text-sm">{order.customer_name} · {order.total_m2} m² · <span className="font-semibold">{fmt(order.total)}</span>{order.delivery_method === 'delivery' && order.delivery_fee == null ? ' (delivery fee to be set)' : ''}</p>
      {!order.email && <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">No email on this order — the customer can't track it online and the invoice will need to be downloaded and sent manually.</p>}
      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={copy} data-testid="copy-order-number"><Copy className="h-4 w-4 mr-1" /> {copied ? 'Copied' : 'Copy number'}</Button>
        <Button className="btn-primary" onClick={onClose} data-testid="manual-order-done">Done</Button>
      </div>
    </div>
  );
}

export function ManualOrderDialog({ open, onOpenChange, onCreated }) {
  const [varieties, setVarieties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([{ variety_id: '', quantity_m2: '' }]);
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setItems([{ variety_id: '', quantity_m2: '' }]); setCreated(null); setQuote(null);
    varietiesApi.getAll().then((r) => setVarieties(r.data)).catch(() => toast.error('Could not load varieties'));
  }, [open]);

  const totalM2 = items.reduce((s, i) => s + (parseFloat(i.quantity_m2) || 0), 0);
  const subtotal = items.reduce((s, i) => { const v = varieties.find((x) => x.id === i.variety_id); return s + (v ? v.price_per_m2 * (parseFloat(i.quantity_m2) || 0) : 0); }, 0);
  const isDelivery = form.delivery_method === 'delivery';

  useEffect(() => {
    if (!isDelivery || (form.suburb.trim().length < 3 && form.postcode.trim().length !== 4)) { setQuote(null); return undefined; }
    const t = setTimeout(() => storeApi.getDeliveryQuote(form.suburb || undefined, form.postcode || undefined, totalM2).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 400);
    return () => clearTimeout(t);
  }, [form.suburb, form.postcode, totalM2, isDelivery]);

  const fee = !isDelivery ? 0 : form.fee_override !== '' ? parseFloat(form.fee_override) || 0 : quote?.matched ? quote.fee : null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (idx, k, v) => setItems((list) => list.map((it, i) => i === idx ? { ...it, [k]: v } : it));

  const submit = async (e) => {
    e.preventDefault();
    const valid = items.filter((i) => i.variety_id && parseFloat(i.quantity_m2) > 0);
    if (!valid.length) { toast.error('Add at least one variety with an area'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name, phone: form.phone, email: form.email || null,
        delivery_method: form.delivery_method, address: form.address || null, suburb: form.suburb || null, postcode: form.postcode || null,
        scheduled_date: form.scheduled_date || null, preferred_date: form.scheduled_date || null, notes: form.notes || null, admin_notes: form.admin_notes || null,
        status: form.status, delivery_fee: isDelivery && form.fee_override !== '' ? parseFloat(form.fee_override) : null,
        items: valid.map((i) => ({ variety_id: i.variety_id, quantity_m2: parseFloat(i.quantity_m2) })),
      };
      const { data } = await ordersApi.create(payload);
      setCreated(data); onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create order'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-order-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> New phone order</DialogTitle></DialogHeader>
        {created ? <OrderNumberReadout order={created} onClose={() => onOpenChange(false)} /> : (
          <form onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Customer name *</Label><Input required value={form.customer_name} onChange={set('customer_name')} data-testid="mo-name" /></div>
              <div className="space-y-1.5"><Label>Phone *</Label><Input required type="tel" value={form.phone} onChange={set('phone')} data-testid="mo-phone" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Email <span className="text-muted-foreground font-normal">(optional — needed for emailed invoice & online tracking)</span></Label><Input type="email" value={form.email} onChange={set('email')} data-testid="mo-email" /></div>
            </div>

            <div className="space-y-2">
              <Label>Turf</Label>
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`mo-item-${idx}`}>
                  <Select value={it.variety_id} onValueChange={(v) => setItem(idx, 'variety_id', v)}>
                    <SelectTrigger className="flex-1" data-testid={`mo-variety-${idx}`}><SelectValue placeholder="Select variety" /></SelectTrigger>
                    <SelectContent>{varieties.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} — ${v.price_per_m2.toFixed(2)}/m²</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="1" step="1" placeholder="m²" value={it.quantity_m2} onChange={(e) => setItem(idx, 'quantity_m2', e.target.value)} className="w-24" data-testid={`mo-qty-${idx}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setItems((l) => l.length > 1 ? l.filter((_, i) => i !== idx) : l)} disabled={items.length === 1} data-testid={`mo-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setItems((l) => [...l, { variety_id: '', quantity_m2: '' }])} data-testid="mo-add-item"><Plus className="h-4 w-4 mr-1" /> Add another variety</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[{ v: 'delivery', label: 'Delivery', icon: Truck }, { v: 'pickup', label: 'Farm pickup', icon: Store }].map((o) => (
                <button key={o.v} type="button" onClick={() => setForm((f) => ({ ...f, delivery_method: o.v }))} className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium border-2 transition-colors ${form.delivery_method === o.v ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50'}`} data-testid={`mo-method-${o.v}`}><o.icon className="h-4 w-4" /> {o.label}</button>
              ))}
            </div>

            {isDelivery && (
              <div className="grid sm:grid-cols-6 gap-3" data-testid="mo-delivery-fields">
                <div className="space-y-1.5 sm:col-span-6"><Label>Street address *</Label><Input required value={form.address} onChange={set('address')} data-testid="mo-address" /></div>
                <div className="space-y-1.5 sm:col-span-3"><Label>Suburb *</Label><Input required value={form.suburb} onChange={set('suburb')} data-testid="mo-suburb" /></div>
                <div className="space-y-1.5 sm:col-span-1"><Label>Postcode</Label><Input value={form.postcode} onChange={set('postcode')} data-testid="mo-postcode" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Delivery fee {quote?.matched && form.fee_override === '' ? <span className="text-muted-foreground font-normal">({quote.zone_name})</span> : ''}</Label><Input type="number" min="0" step="0.01" placeholder={quote?.matched ? String(quote.fee) : 'quote'} value={form.fee_override} onChange={set('fee_override')} data-testid="mo-fee" /></div>
                {isDelivery && (form.suburb || form.postcode) && !quote?.matched && form.fee_override === '' && <p className="sm:col-span-6 text-xs text-amber-700" data-testid="mo-no-zone">Suburb isn't in a delivery zone — type a fee or leave blank to set it later.</p>}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{isDelivery ? 'Delivery' : 'Pickup'} date</Label><Input type="date" value={form.scheduled_date} onChange={set('scheduled_date')} data-testid="mo-date" /></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="mo-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{['new', 'confirmed', 'scheduled'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.admin_notes} onChange={set('admin_notes')} placeholder="Gate code, call before arriving…" data-testid="mo-notes" /></div>

            <div className="rounded-md bg-secondary/40 p-3 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="mo-summary">
              <span>{totalM2.toFixed(0)} m²</span>
              <span>Turf {fmt(subtotal)}</span>
              <span>{isDelivery ? (fee == null ? 'Delivery: to be quoted' : `Delivery ${fmt(fee)}`) : 'Pickup: free'}</span>
              <span className="font-bold ml-auto" data-testid="mo-total">Total {fmt(subtotal + (fee || 0))}</span>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={saving} data-testid="mo-submit">{saving ? 'Creating…' : 'Create order & get number'}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
